import { supabase, run } from './db';

/**
 * Configuração do laboratório: dados cadastrais, preços padrão e logo.
 *
 * SALVA POR FUNÇÃO, não por `update` na tabela. O UPDATE de `laboratories` é
 * só do dono, e abri-lo para todo admin daria junto `owner_id` e `status`. A
 * função `salvar_configuracao_do_laboratorio` escreve só as colunas desta
 * tela, e só as chaves que vierem.
 */

/**
 * Os preços que o laboratório pode deixar como padrão, na ordem da tela.
 *
 * A chave é a MESMA coluna de `services` que o formulário preenche — é isso
 * que deixa `precosParaServicoNovo` ser um espalhamento, sem tabela de
 * tradução para sair de sincronia. A função do banco aceita só estas chaves.
 */
export const PRECOS_PADRAO = [
  { key: 'value_per_te',        label: 'Valor por TE' },
  { key: 'value_per_pregnancy', label: 'Valor por prenhez' },
  { key: 'daily_value',         label: 'Valor da diária' },
  { key: 'package_value',       label: 'Valor do pacote' },
  { key: 'value_per_embryo',    label: 'Valor por embrião' },
  { key: 'preco_vitrificacao',  label: 'Vitrificação (por embrião)' },
  { key: 'preco_dt',            label: 'DT (por embrião)' },
  { key: 'preco_desvitrificacao', label: 'Desvitrificação, embrião de outro laboratório' },
  { key: 'price_per_km',        label: 'Deslocamento (R$/km)' },
];

/**
 * O que o formulário de serviço NOVO já traz preenchido.
 *
 * Só preenche: o valor vai para o campo e ali continua editável, e o que vale
 * é o que for salvo no serviço. Nada lê `precos_padrao` para cobrar, então
 * mudar o padrão não reescreve serviço nenhum.
 *
 * Zero não preenche. Um padrão zerado é "não configurado", e preencher zero
 * esconderia o exemplo cinza do campo atrás de um número que ninguém escolheu.
 */
export const precosParaServicoNovo = (laboratorio) => {
  const precos = laboratorio?.precos_padrao || {};
  const saida = {};
  PRECOS_PADRAO.forEach(({ key }) => {
    const v = Number(precos[key]);
    if (Number.isFinite(v) && v > 0) saida[key] = v;
  });
  return saida;
};

export const salvarConfiguracao = (laboratoryId, dados) =>
  run(supabase.rpc('salvar_configuracao_do_laboratorio', {
    p_laboratory_id: laboratoryId,
    p_dados: dados,
  }));

// ── Logo ─────────────────────────────────────────────────────────────

/** Espelha `allowed_mime_types` do bucket. Sem SVG: SVG pode carregar script. */
export const TIPOS_DA_LOGO = ['image/png', 'image/jpeg', 'image/webp'];
export const TAMANHO_DA_LOGO = 2 * 1024 * 1024;

const EXTENSAO = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };

/** Recusa aqui, em português — o Storage recusaria em inglês e sem dizer o porquê. */
export const problemaDaLogo = (arquivo) => {
  if (!arquivo) return 'Nenhum arquivo escolhido.';
  if (!TIPOS_DA_LOGO.includes(arquivo.type)) return 'A logo precisa ser PNG, JPG ou WebP.';
  if (arquivo.size > TAMANHO_DA_LOGO) return 'A logo passa de 2 MB. Exporte numa resolução menor.';
  return null;
};

/**
 * Nome novo a cada envio, na pasta do laboratório.
 *
 * Mesmo nome com upsert deixaria o navegador e a CDN mostrando a logo antiga
 * por horas — a URL não mudou. Com o instante no nome, trocar a logo é trocar
 * a URL. A pasta é o laboratório porque é ela que a política do bucket confere.
 */
export const caminhoDaLogo = (laboratoryId, tipo, agora = Date.now()) =>
  `${laboratoryId}/logo-${agora}.${EXTENSAO[tipo] || 'png'}`;

/**
 * Sobe a logo e grava o caminho no laboratório. Devolve o laboratório salvo.
 *
 * SEM COMPRIMIR, ao contrário do comprovante: a compressão sai em JPEG, e logo
 * em PNG quase sempre tem fundo transparente, que viraria um retângulo preto.
 *
 * A antiga só é apagada depois que a nova está gravada no laboratório. Falhar
 * no meio deixa um arquivo órfão no bucket, nunca um laboratório apontando
 * para uma logo que não existe.
 */
export const enviarLogo = async (laboratoryId, arquivo, logoAnterior) => {
  const problema = problemaDaLogo(arquivo);
  if (problema) throw new Error(problema);

  const path = caminhoDaLogo(laboratoryId, arquivo.type);
  const { error } = await supabase.storage
    .from('logos')
    .upload(path, arquivo, { contentType: arquivo.type });
  if (error) throw error;

  const lab = await salvarConfiguracao(laboratoryId, { logo_path: path });
  if (logoAnterior) await apagarArquivo(logoAnterior);
  return lab;
};

export const removerLogo = async (laboratoryId, logoAtual) => {
  const lab = await salvarConfiguracao(laboratoryId, { logo_path: null });
  if (logoAtual) await apagarArquivo(logoAtual);
  return lab;
};

// Arquivo que sobra no bucket não quebra nada; logo que some quebra. Por isso
// a falha aqui é aviso, e não erro.
const apagarArquivo = async (path) => {
  const { error } = await supabase.storage.from('logos').remove([path]);
  if (error) console.warn('[logo] a antiga ficou no bucket:', error.message);
};

/**
 * URL pública da logo. O bucket é público: a logo vai impressa para o
 * cliente, e a área do cliente por link não tem sessão para assinar URL.
 */
export const urlDaLogo = (path) =>
  path ? supabase.storage.from('logos').getPublicUrl(path).data?.publicUrl || null : null;
